// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { useMedplum } from '@medplum/react';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import { useEffect, useState } from 'react';
import { findProviderAppointments } from './availability-search';
import type { ProviderAppointmentCandidate } from './types';

const MAX_AVAILABILITY_ATTEMPTS = 2;
const AVAILABILITY_RETRY_DELAY_MS = 250;

export function useAvailableAppointments(params: {
  appointmentType: AppointmentTypeDefinition | undefined;
  practitionerId?: string;
  startDate: string;
  endDate: string;
}): { appointments: ProviderAppointmentCandidate[]; loading: boolean; error: Error | null; ready: boolean } {
  const medplum = useMedplum();
  const { appointmentType, practitionerId, startDate, endDate } = params;
  const [appointments, setAppointments] = useState<ProviderAppointmentCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!appointmentType) {
      setAppointments([]);
      setLoading(false);
      setError(null);
      return;
    }
    const selectedAppointmentType = appointmentType;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    async function loadAppointments(): Promise<void> {
      let lastError: unknown;
      for (let attempt = 0; attempt < MAX_AVAILABILITY_ATTEMPTS; attempt += 1) {
        if (controller.signal.aborted) {
          return;
        }

        try {
          const result = await findProviderAppointments(medplum, {
            appointmentType: selectedAppointmentType,
            practitionerId,
            startDate,
            endDate,
            signal: controller.signal,
          });
          if (!controller.signal.aborted) {
            setAppointments(result);
            setError(null);
            setLoading(false);
          }
          return;
        } catch (err: unknown) {
          if (controller.signal.aborted) {
            return;
          }
          lastError = err;
          if (attempt + 1 < MAX_AVAILABILITY_ATTEMPTS) {
            await new Promise<void>((resolve) => {
              const timeoutId = window.setTimeout(resolve, AVAILABILITY_RETRY_DELAY_MS);
              controller.signal.addEventListener(
                'abort',
                () => {
                  window.clearTimeout(timeoutId);
                  resolve();
                },
                { once: true }
              );
            });
          }
        }
      }

      if (!controller.signal.aborted) {
        setAppointments([]);
        setError(lastError instanceof Error ? lastError : new Error('Unable to load appointments.'));
        setLoading(false);
      }
    }

    loadAppointments().catch((err: unknown) => {
      if (!controller.signal.aborted) {
        setAppointments([]);
        setError(err instanceof Error ? err : new Error('Unable to load appointments.'));
        setLoading(false);
      }
    });

    return () => controller.abort();
  }, [appointmentType, endDate, medplum, practitionerId, startDate]);
  return { appointments, loading, error, ready: Boolean(appointmentType && !loading && !error) };
}
